export const PERMISSION_ACTIONS = ["view", "add", "edit", "delete"];

export const PERMISSION_MODULES = [
  { key: "dashboard", label: "Dashboard" },
  {
    key: "visitor",
    label: "Visitor",
    children: [
      { key: "visitor_passes", label: "Visitor Passes" },
      { key: "student_gate_pass", label: "Student Gate Pass" },
      { key: "staff_out_pass", label: "Staff Out Pass" },
    ],
  },
  {
    key: "front_office",
    label: "Front Office",
    children: [
      { key: "applications", label: "Applications" },
      { key: "phone_call_logs", label: "Phone Call Log" },
      { key: "postal_dispatch", label: "Postal Dispatch" },
      { key: "postal_receive", label: "Postal Receive" },
      { key: "complaints", label: "Complaints" },
      { key: "lost_found", label: "Lost & Found" },
    ],
  },
  {
    key: "students",
    label: "Students",
    children: [
      { key: "directory", label: "Directory" },
      { key: "promote", label: "Promote" },
      { key: "ptms", label: "PTM" },
      { key: "ptm_attendance", label: "PTM Attendance" },
    ],
  },
  { key: "attendance", label: "Attendance" },
  {
    key: "fees",
    label: "Fees",
    children: [
      { key: "receipt", label: "Collect Fee" },
      { key: "receipts", label: "Receipts" },
      { key: "reconciliation", label: "Cheque Reconciliation" },
      { key: "assign", label: "Assign Fees" },
      { key: "misc_assign", label: "Miscellaneous Fees" },
      { key: "outstanding", label: "Outstanding" },
      { key: "concession", label: "Concession" },
      { key: "opening", label: "Opening Balance" },
      { key: "student_ledger", label: "Student Ledger" },
      { key: "ledger", label: "Ledger Report" },
      { key: "vouchers", label: "Vouchers" },
      { key: "fee_types", label: "Fee Types" },
      { key: "fee_master", label: "Fee Master" },
      { key: "ledger_master", label: "Ledger Master" },
    ],
  },
  {
    key: "staff",
    label: "Staff",
    children: [
      { key: "directory", label: "Staff Directory" },
      { key: "attendance", label: "Staff Attendance" },
      { key: "leave_requests", label: "Leave Requests" },
      { key: "approve_leave", label: "Approve Leave" },
      { key: "leave_types", label: "Leave Types" },
      { key: "leave_stats", label: "Leave Stats" },
      { key: "payroll", label: "Payroll" },
      { key: "teacher_ratings", label: "Teacher Ratings" },
      { key: "departments", label: "Departments" },
      { key: "designations", label: "Designations" },
    ],
  },
  {
    key: "academic",
    label: "Academic",
    children: [
      { key: "classes", label: "Classes" },
      { key: "sections", label: "Sections" },
      { key: "subjects", label: "Subjects" },
      { key: "subject_groups", label: "Subject Groups" },
      { key: "period_master", label: "Period Master" },
      { key: "timetables", label: "Timetables" },
      { key: "houses", label: "Houses" },
      { key: "clubs", label: "Clubs" },
      { key: "categories", label: "Categories" },
    ],
  },
  { key: "calendar", label: "Calendar" },
  { key: "allotment", label: "Allotment" },
  { key: "lesson_plan", label: "Lesson Plan" },
  {
    key: "assignment",
    label: "Assignment",
    children: [
      { key: "assignments", label: "Assignments" },
      { key: "submissions", label: "Submissions" },
      { key: "nb_correction", label: "NB Correction" },
    ],
  },
  { key: "library", label: "Library" },
  { key: "inventory", label: "Inventory" },
  { key: "transport", label: "Transport" },
  {
    key: "communicate",
    label: "Communicate",
    children: [
      { key: "circulars", label: "Circulars" },
      { key: "student_circulars", label: "Student Circulars" },
      { key: "chats", label: "Chats" },
      { key: "helpdesk", label: "Helpdesk" },
      { key: "downloads", label: "Download Center" },
    ],
  },
  { key: "reports", label: "Reports" },
  { key: "settings", label: "Settings" },
];

let rolePermissionsCache = null;
let rolePermissionsPromise = null;

export function normalizePermissionKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
}

export function emptyPermissionSet() {
  return { view: false, add: false, edit: false, delete: false };
}

export function findRolePermissionEntry(allPermissions, roleName) {
  if (!allPermissions || typeof allPermissions !== "object") {
    return null;
  }

  const direct = allPermissions[roleName];
  if (direct !== undefined) {
    return direct;
  }

  const normalizedRole = normalizePermissionKey(roleName);
  for (const [key, value] of Object.entries(allPermissions)) {
    if (normalizePermissionKey(key) === normalizedRole) {
      return value;
    }
  }

  return null;
}

function permissionSetFromValue(value) {
  const empty = emptyPermissionSet();
  if (value === true) {
    return { view: true, add: true, edit: true, delete: true };
  }
  if (!value) {
    return empty;
  }
  if (Array.isArray(value)) {
    const allowed = value.map((item) => normalizePermissionKey(item));
    return {
      view: allowed.includes("*") || allowed.includes("view"),
      add: allowed.includes("*") || allowed.includes("add"),
      edit: allowed.includes("*") || allowed.includes("edit"),
      delete: allowed.includes("*") || allowed.includes("delete"),
    };
  }
  if (typeof value === "object") {
    const resolved = { ...empty };
    for (const action of PERMISSION_ACTIONS) {
      resolved[action] = !!value[action];
    }
    return resolved;
  }

  return empty;
}

export function hasAnyPermission(permissionSet) {
  return PERMISSION_ACTIONS.some((action) => !!permissionSet?.[action]);
}

export function resolvePermissionSet(
  roleEntry,
  moduleKey,
  submoduleKey,
  isSuperAdmin = false,
) {
  if (isSuperAdmin) {
    return { view: true, add: true, edit: true, delete: true };
  }

  const normalizedModuleKey = normalizePermissionKey(moduleKey);
  const normalizedSubmoduleKey = submoduleKey
    ? normalizePermissionKey(submoduleKey)
    : "";
  const empty = emptyPermissionSet();

  if (roleEntry == null) {
    return empty;
  }

  if (Array.isArray(roleEntry)) {
    const allowed = roleEntry.map((item) => normalizePermissionKey(item));
    if (
      allowed.includes("*") ||
      allowed.includes(normalizedModuleKey) ||
      (normalizedSubmoduleKey && allowed.includes(normalizedSubmoduleKey))
    ) {
      return { view: true, add: true, edit: true, delete: true };
    }
    return empty;
  }

  if (typeof roleEntry !== "object") {
    return empty;
  }

  if (roleEntry["*"] === true) {
    return { view: true, add: true, edit: true, delete: true };
  }

  const modulePerm =
    roleEntry[normalizedModuleKey] ??
    roleEntry[normalizedModuleKey.replace(/_/g, "-")];

  if (normalizedSubmoduleKey && modulePerm && typeof modulePerm === "object") {
    const subValue =
      modulePerm[normalizedSubmoduleKey] ??
      modulePerm[normalizedSubmoduleKey.replace(/_/g, "-")];
    if (subValue !== undefined) {
      return permissionSetFromValue(subValue);
    }
  }

  return permissionSetFromValue(modulePerm);
}

export function hasModuleAccess(roleEntry, moduleKey, isSuperAdmin = false) {
  const moduleConfig = PERMISSION_MODULES.find(
    (item) =>
      normalizePermissionKey(item.key) === normalizePermissionKey(moduleKey),
  );

  const direct = resolvePermissionSet(roleEntry, moduleKey, null, isSuperAdmin);
  if (hasAnyPermission(direct)) {
    return true;
  }

  for (const child of moduleConfig?.children || []) {
    const nested = resolvePermissionSet(
      roleEntry,
      moduleKey,
      child.key,
      isSuperAdmin,
    );
    if (hasAnyPermission(nested)) {
      return true;
    }
  }

  return false;
}

export async function loadRolePermissions(api) {
  if (rolePermissionsCache !== null) {
    return rolePermissionsCache;
  }
  if (rolePermissionsPromise) {
    return rolePermissionsPromise;
  }

  rolePermissionsPromise = api
    .get("/settings/role_permissions")
    .then(({ data }) => {
      rolePermissionsCache =
        data && typeof data.value === "object" ? data.value : {};
      rolePermissionsPromise = null;
      return rolePermissionsCache;
    })
    .catch(() => {
      rolePermissionsPromise = null;
      rolePermissionsCache = {};
      return rolePermissionsCache;
    });

  return rolePermissionsPromise;
}

export function clearRolePermissionsCache() {
  rolePermissionsCache = null;
  rolePermissionsPromise = null;
}
